import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UboHelpComponent } from '@shared/dialog-contents/ubo-help/ubo-help.component';
import { collectFormErrors } from '@shared/formHelpers';
import { Application, UBOProvisioningChoice } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { ModalDialogService } from '@shared/services/modal-dialog.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'app-beneficial-owner-choice',
    templateUrl: './beneficial-owner-choice.component.html',
    styleUrls: ['./beneficial-owner-choice.component.scss']
})
export class BeneficialOwnerChoiceComponent implements OnInit {
    private readonly stepId = REGISTRATION_STEP_ID.UBO_CHOICE;
    private applicationDTO: Application;
    form: FormGroup<{ uboMode: FormControl<UBOProvisioningChoice> }>;
    loading: boolean = true;
    countryOfIncorporation: string;

    constructor(
        protected logger: NGXLogger,
        private readonly fb: UntypedFormBuilder,
        private readonly modalDialogService: ModalDialogService,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    get UBOProvisioningChoice() {
        return UBOProvisioningChoice;
    }

    ngOnInit(): void {
        const application = this.route.snapshot.data.application;
        this.applicationDTO = application;
        this.countryOfIncorporation = application.company.registeredAddress?.countryISO;
        this.form = this.fb.group({
            "uboMode": new FormControl<UBOProvisioningChoice>(this.applicationDTO.uboProvisioningChoice, [Validators.required])
        });
        this.loading = false;
    }

    openUBOHelpDialog(): void {
        this.modalDialogService.showModalDialog(UboHelpComponent, {
            title: "MODAL_CONTENTS.UBO_HELP.TITLE",
            dismissLabel: "MODAL_CONTENTS.UBO_HELP.BUTTONS.DISMISS"
        });
    }

    onSubmit(): boolean {

        this.form.controls.uboMode.markAsTouched();

        if (this.form.valid) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            const applicationToSave = RegistrationHelper.cloneApplication(this.applicationDTO);
            applicationToSave.uboProvisioningChoice = this.form.value.uboMode;
            this.registrationService.saveApplication(applicationToSave).then(() => {
                this.amplitudeService.trackBeneficialOwnerInputModeChoice(applicationToSave.uboProvisioningChoice);
                this.registrationService.completeStep(this.stepId);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            }, (err) => {
                this.logger.trace("FATAL: ", err);
                this.dynatrace.leaveAction(submitActionId);
            });
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }

        return true;
    }

    goToPrevious() {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

}
